import { get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { Divider } from 'web-ui-components/lib/atoms/layout';
import { Title } from 'web-ui-components/lib/atoms/text';
import { Container, FlexColumn, FlexRow, ServicingSection } from 'web-ui-components/lib/global/layout';
import { ButtonGroup } from 'web-ui-components/lib/molecules/buttons';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { ProminentLabel } from 'web-ui-components/lib/typography/labels';
import { CONST_FILE_UPLOAD, CONST_HARD, CONST_SOFT, CONST_THIRDPARTY } from '../../../config/constants';
import { getAuthorisationMessage } from '../../../helpers/fieldHelpers';
import BacsStatusBlock from '../BacsStatusBlock/BacsStatusBlock';

export const getAuthorisedRequiredBody = (
	getAuthorisedRequiredBodyContent,
	authType,
	paymentError,
	authorisers,
	isFileUploadBatch = false,
	handleRoute,
	userId
) => {
	const {
		sidebarBodyHardToken,
		sidebarBodySoftToken,
		fileUploadTwoStepInstructionHeading,
		fileUploadFirstInstruction,
		fileUploadSecondInstruction,
		fileUploadThirdInstruction,
		fileUploadFourthInstruction,
		fileUploadFifthInstruction,
		fileUploadReviewButton,
	} = getAuthorisedRequiredBodyContent;
	if (get(paymentError, 'message')) {
		return get(paymentError, 'message');
	}
	switch (authType) {
		case CONST_THIRDPARTY:
			return getAuthorisationMessage(authorisers, userId);
		case CONST_HARD:
			if (authorisers.length > 1) return getAuthorisationMessage(authorisers, userId);
			return sidebarBodyHardToken;
		default:
			if (authorisers.length > 1 && !isFileUploadBatch) {
				return (
					<div>
						<p>{sidebarBodySoftToken}</p>
						<p>{getAuthorisationMessage(authorisers, userId)}</p>
					</div>
				);
			}
			if (isFileUploadBatch) {
				return (
					<div>
						<center>
							<ProminentLabel>{fileUploadTwoStepInstructionHeading}</ProminentLabel>
							<p>{fileUploadFirstInstruction}</p>
							<ProminentLabel>{fileUploadSecondInstruction}</ProminentLabel>
							<p>{fileUploadThirdInstruction}</p>
							<ProminentLabel>{fileUploadFourthInstruction}</ProminentLabel>
							<p>{fileUploadFifthInstruction}</p>
							{isFileUploadBatch && authType !== CONST_THIRDPARTY && (
								<Button onClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')} display="primary" width="flush">
									{fileUploadReviewButton}
								</Button>
							)}
						</center>
					</div>
				);
			}
			return sidebarBodySoftToken;
	}
};

const BacsSubmittedPage = ({
	content: {
		titleNoAuth,
		titleAuthReq,
		descriptionNoAuth,
		descriptionAuthReq,
		goToYourAccountButton,
		makeAnotherPaymentButton,
		sidebarTitle,
		hardTokenAuthButton,
		paymentFailedTitle,
		getAuthorisedRequiredBodyContent,
	},
	bacsDetails,
	handleRoute,
	handleReset,
	authRequired,
	authType,
	renderBacsSummary,
	renderBacsTable,
	paymentError,
	authorisers,
	userId,
}) => {
	const isFileUploadBatch = get(bacsDetails, 'batch_type', '') === CONST_FILE_UPLOAD.BATCH_TYPE;
	const showSideBar = authRequired || paymentError;
	const sideBarStatusType = authRequired ? 'authorisation' : 'failed';
	const decideSidebarTitle = !(isFileUploadBatch && authType === CONST_SOFT) ? sidebarTitle : '';
	const sideBarStatusTitle = authRequired ? decideSidebarTitle : paymentFailedTitle;
	const sideBarBody = getAuthorisedRequiredBody(
		getAuthorisedRequiredBodyContent,
		authType,
		paymentError,
		authorisers,
		isFileUploadBatch,
		handleRoute,
		userId
	);
	return (
		<div>
			<Container className="u-padding-y-2" size="lg">
				<PageHeading isFlush data-test-id="bacs-submitted-page">
					{authRequired ? titleAuthReq : titleNoAuth}
				</PageHeading>
				<p className="u-margin-top-1">{authRequired ? descriptionAuthReq : descriptionNoAuth}</p>
			</Container>
			<ServicingSection padded={false}>
				<div className="u-background-color-grey-tint-95 u-padding-4">
					<Title size="h4">{bacsDetails.paymentName}</Title>
					<Divider />
					<FlexRow>
						<FlexColumn gutter={1}>
							<div className="u-flex-full-height">{renderBacsSummary()}</div>
						</FlexColumn>
						{showSideBar && (
							<FlexColumn gutter={1}>
								<BacsStatusBlock title={sideBarStatusTitle} type={sideBarStatusType}>
									{sideBarBody}
									{authType === CONST_HARD && (
										<Button onClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')}>{hardTokenAuthButton}</Button>
									)}
								</BacsStatusBlock>
							</FlexColumn>
						)}
					</FlexRow>
					{renderBacsTable()}
				</div>
			</ServicingSection>

			<Container className="u-padding-y-2" size="lg">
				<ButtonGroup>
					<Button id="goToMyAccountBtn" display="secondary" onClick={() => handleRoute('WEB-HOMEPAGE')}>
						{goToYourAccountButton}
					</Button>
					<Button id="resetBtn" display="primary" onClick={handleReset}>
						{makeAnotherPaymentButton}
					</Button>
				</ButtonGroup>
			</Container>
		</div>
	);
};
BacsSubmittedPage.propTypes = {
	paymentError: PropTypes.shape({}),
	bacsDetails: PropTypes.shape({}).isRequired,
	content: PropTypes.shape({}).isRequired,
	handleRoute: PropTypes.func.isRequired,
	renderBacsSummary: PropTypes.func.isRequired,
	renderBacsTable: PropTypes.func.isRequired,
	handleReset: PropTypes.func.isRequired,
	authRequired: PropTypes.bool.isRequired,
	authType: PropTypes.string.isRequired,
	authorisers: PropTypes.arrayOf(PropTypes.shape({})),
};
export default BacsSubmittedPage;
